import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import { shallow } from 'zustand/shallow';
import { useSnackbar } from 'notistack';
import {
  cancelationReasonsExternalUser,
  cancelationReasonsInternalUser,
} from '@components/modals/CancelationModal/CancelationModal.constants';
import { useStore } from '@store/GlobalStore';
import { useCancelQuote } from '@services/quotes';
import { Dialog } from '@components/dialog';
import * as styles from '@components/modals/Modal.styles';
import { ToastType } from '@components/toast';
import { FormSection, Form } from '@components/forms';
import { getFormFields } from './getFormFields';
import { CancellationModalFormValues } from '@components/modals/CancelationModal/CancellationModalFormValues.type';
import { QuoteStatusChangeReason } from '@models/quoteStatusChange';

type CancelationReasonDetails = {
  carrier?: string;
  otherCarrier?: string;
  duplicate?: string;
  premium?: string | number;
};

// `quoteStatusChangeReason.details` is loosely typed by the API (string | number | boolean |
// object | array), so narrow it at runtime instead of asserting a shape it may not have.
const toReasonDetails = (details: unknown): CancelationReasonDetails => {
  if (typeof details !== 'object' || details === null || Array.isArray(details)) return {};
  const record = details as Record<string, unknown>;
  const asString = (value: unknown) => (typeof value === 'string' ? value : undefined);
  return {
    carrier: asString(record.carrier),
    otherCarrier: asString(record.otherCarrier),
    duplicate: asString(record.duplicate),
    premium: typeof record.premium === 'number' || typeof record.premium === 'string' ? record.premium : undefined,
  };
};

type CancelationModalEditableProps = {
  isReadOnly?: false;
  isOpen: boolean;
  policyNumber: string;
  quoteId: string;
  onCloseModal: () => void;
  onSuccess?: () => void;
};

// Read-only view opened from a Cancelled milestone: populated from the stored
// `quoteStatusChangeReason`, so none of the submit-flow props are required.
type CancelationModalReadOnlyProps = {
  isReadOnly: true;
  isOpen: boolean;
  onCloseModal: () => void;
  policyNumber?: string;
  reasonData: QuoteStatusChangeReason;
};

export type CancelationModalProps = CancelationModalEditableProps | CancelationModalReadOnlyProps;

export const CancelationModal: React.FC<CancelationModalProps> = (props) => {
  const { isOpen, onCloseModal, policyNumber } = props;
  const isReadOnly = props.isReadOnly === true;
  const reasonData = props.isReadOnly ? props.reasonData : undefined;
  const editable = props.isReadOnly ? undefined : props;

  const { cancelQuote, isPending: isLoading } = useCancelQuote();
  const isInternalUser = useStore((state) => state.userAuth.isInternalUser, shallow);
  const { enqueueSnackbar } = useSnackbar();

  const readOnlyDetails = toReasonDetails(reasonData?.details);

  const methods = useForm<CancellationModalFormValues>({
    defaultValues: reasonData
      ? {
          reason: reasonData.reason,
          notes: reasonData.description ?? '',
          carrier: readOnlyDetails.carrier ?? '',
          otherCarrier: readOnlyDetails.otherCarrier ?? '',
          duplicateSource: readOnlyDetails.duplicate ?? '',
          premium: readOnlyDetails.premium != null ? Number(readOnlyDetails.premium) : undefined,
        }
      : {},
    mode: isReadOnly ? 'onSubmit' : 'onChange',
  });

  const clearForm = () => {
    methods.reset({});
  };

  const handleExit = () => {
    clearForm();
    onCloseModal();
  };

  const handleCancelQuote = async () => {
    if (!editable || !editable.quoteId) {
      return;
    }

    const formData = methods.getValues();
    const cancelQuoteBody = {
      quoteId: editable.quoteId,
      reason: formData.reason,
      description: formData.notes,
      details: {
        carrier: formData.carrier,
        otherCarrier: formData.otherCarrier,
        duplicate: formData.duplicateSource,
        premium: formData.premium,
      },
    };

    cancelQuote(cancelQuoteBody, {
      onError: () => {
        enqueueSnackbar(`Cancellation of quote #${policyNumber} has failed.`, {
          preventDuplicate: true,
          variant: ToastType.Error,
        });
      },
      onSuccess: () => {
        clearForm();
        editable.onSuccess?.();
        onCloseModal();
        enqueueSnackbar(`Quote #${policyNumber} was successfully cancelled.`, {
          variant: ToastType.Success,
          preventDuplicate: true,
        });
      },
    });
  };

  // The read-only view is internal-only, so use the internal reason list there as well so any
  // previously-stored reason renders its label in the disabled Select.
  const cancelationReasons =
    isReadOnly || isInternalUser ? cancelationReasonsInternalUser : cancelationReasonsExternalUser;

  const fields = useMemo(() => {
    const allFields = getFormFields({ cancelationReasons });
    if (!isReadOnly) return allFields;

    // Hide the confirmation checkbox, which is a workflow-only input.
    const hiddenInReadOnly = ['confirmation'];
    return Object.fromEntries(Object.entries(allFields).filter(([name]) => !hiddenInReadOnly.includes(name)));
  }, [cancelationReasons, isReadOnly]);

  // Only read `formState.isValid` for the editable flow — accessing it makes react-hook-form
  // run validation on the (disabled, required) fields, which is pointless in read-only mode.
  const isValid = isReadOnly ? true : methods.formState.isValid;

  const title = isReadOnly
    ? policyNumber
      ? `Cancellation Reasons for Quote #${policyNumber}`
      : 'Cancellation Reasons'
    : `Cancellation of Quote #${policyNumber}`;

  return (
    <Dialog
      handleCloseDialog={isReadOnly ? onCloseModal : handleExit}
      isPrimaryBtnDisabled={!isValid}
      isPrimaryBtnLoading={isLoading}
      open={isOpen}
      primaryBtnText={isReadOnly ? undefined : 'Cancel Quote'}
      primaryBtnAction={isReadOnly ? undefined : handleCancelQuote}
      secondaryBtnAction={isReadOnly ? onCloseModal : handleExit}
      secondaryBtnText='Exit'
      title={title}
    >
      <Form methods={methods} formId='cancellation-modal-form'>
        <Box sx={{ ...styles.modalContent, paddingTop: 0, paddingBottom: 0 }}>
          <Box sx={styles.modalScrollContainer}>
            <FormSection fields={fields} styleConfig={{ spacing: 3 }} isReadOnly={isReadOnly} />
          </Box>
        </Box>
      </Form>
    </Dialog>
  );
};
